import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useLanguage } from "../../context/LanguageContext";
import { ArrowRight, ArrowLeft, Package, Truck, Shield, Award, Phone, ChevronRight, ChevronLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "218915224966";

// Company Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_bahr-rimal-system/artifacts/xdruqubd_IMG_6467.JPG";

// Lynae Product Images - Updated with new images
const LYNAE_IMAGES = {
  vitamin_b12: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/nwegxc9h_IMG_6472.jpg",
  omega_369: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/wo3f24qp_IMG_6473.jpg",
  coq10: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/kr9tnjrz_IMG_6475.jpg",
  selenium: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/pvbauurt_IMG_6476.jpg",
};

const LYNAE_SHOWCASE = {
  products1: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/q6goza6r_IMG_6468.jpg",
  products2: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/k6w2pfvj_IMG_6484.jpg",
};

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_90f306be-14e8-4243-9027-a65ac12e2395/artifacts/scwdojd2_IMG_6468.jpg",
  aboutProducts: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/q6goza6r_IMG_6468.jpg",
};

const HomePage = () => {
  const { t, isRTL } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  const features = [
    {
      icon: Package,
      title: t("qualityProducts"),
      description: t("qualityDesc"),
    },
    {
      icon: Truck,
      title: t("fastDelivery"),
      description: t("fastDeliveryDesc"),
    },
    {
      icon: Shield,
      title: t("certifiedSafe"),
      description: t("certifiedSafeDesc"),
    },
    {
      icon: Award,
      title: t("since2012"),
      description: t("since2012Desc"),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products?is_featured=true`),
        ]);
        setCategories(catRes.data.slice(0, 6));
        setFeaturedProducts(prodRes.data.slice(0, 6));
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Medical supplies and medications"
            className="w-full h-full object-cover blur-sm scale-105"
          />
          <div className="absolute inset-0 bg-[#0f172a]/80" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            {/* Logo Badge */}
            <div className="flex items-center gap-4 mb-6">
              <img 
                src={LOGO_URL} 
                alt="بحر الرمال" 
                className="w-20 h-20 rounded-full object-cover border-4 border-[#38bdf8] shadow-xl"
              />
              <Badge className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90 text-sm px-4 py-2">
                {isRTL ? "تأسست 2012 • طرابلس، ليبيا" : "Est. 2012 • Tripoli, Libya"}
              </Badge>
            </div>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              {isRTL ? "شركة بحر الرمال" : "Bahr Al Rimal"}
            </h1>
            <h2 className="text-2xl md:text-3xl text-[#d4af37] font-heading font-semibold mb-6">
              {isRTL ? "لإستيراد الأدوية والمعدات الطبية" : "Medical Supplies Import & Distribution"}
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              {t("heroDescription")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90 h-14 px-8 text-lg font-semibold" data-testid="hero-products-btn">
                  {t("browseProducts")}
                  <ArrowIcon className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
                </Button>
              </Link>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0f172a] h-14 px-8 text-lg" data-testid="hero-whatsapp-btn">
                  <Phone className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5`} />
                  {t("contactUs")}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#f8fafc]" data-testid="features-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#1a365d]/10 rounded-full flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-[#1a365d]" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lynae Products Showcase */}
      <section className="py-24 bg-[#0f172a]" data-testid="lynae-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#d4af37] text-[#0f172a]">
              {isRTL ? "الموزع الرئيسي" : "Main Distributor"}
            </Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              {isRTL ? "منتجات لينى للفيتامينات والمكملات" : "Lynae Vitamins & Supplements"}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {isRTL 
                ? "فيتامينات ومكملات غذائية عالية الجودة من الولايات المتحدة الأمريكية. شركة بحر الرمال هي الموزع الرئيسي الحصري في ليبيا."
                : "Premium quality vitamins and supplements from the USA. Bahr Al Rimal is the exclusive main distributor in Libya."}
            </p>
          </div>
          
          {/* Lynae Product Images Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {Object.values(LYNAE_IMAGES).map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative aspect-square rounded-2xl overflow-hidden shadow-2xl group"
              >
                <img
                  src={img}
                  alt={`Lynae Product ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </div>
          
          {/* Product Showcase Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative aspect-square md:aspect-video rounded-2xl overflow-hidden shadow-2xl group"
            >
              <img
                src={LYNAE_SHOWCASE.products1}
                alt="Lynae Products - DHEA, CoQ10, Zinc, Vitamin D"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white font-semibold text-lg">LYNAE® Premium Supplements</p>
                <p className="text-white/80 text-sm">DHEA • CoQ10 • Zinc • Vitamin D</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative aspect-square md:aspect-video rounded-2xl overflow-hidden shadow-2xl group"
            >
              <img
                src={LYNAE_SHOWCASE.products2}
                alt="Lynae Products - Selenium Yeast, Vitamin B-12, Magnesium, Vitamin D3"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white font-semibold text-lg">LYNAE® Vitamins & Minerals</p>
                <p className="text-white/80 text-sm">Selenium • B-12 • Magnesium • D3</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24" data-testid="categories-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex justify-between items-end mb-12">
            <div>
              <Badge className="mb-4 bg-[#1a365d]/10 text-[#1a365d]">{t("categories")}</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                {t("browseByCategory")}
              </h2>
            </div>
            <Link to="/products" className="hidden md:flex items-center text-[#1a365d] hover:text-[#d4af37] transition-colors font-medium">
              {t("viewAll")}
              <ChevronIcon className={`${isRTL ? 'mr-1' : 'ml-1'} h-5 w-5`} />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <div key={i} className="aspect-square rounded-xl bg-muted animate-pulse" />
              ))
            ) : (
              categories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/products?category=${category.id}`}>
                    <Card className="group cursor-pointer overflow-hidden border-2 border-transparent hover:border-[#1a365d]/20 transition-all card-interactive">
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#1a365d]/10 to-[#d4af37]/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Package className="w-8 h-8 text-[#1a365d]" />
                        </div>
                        <h3 className="font-heading font-semibold text-sm">
                          {isRTL && category.name_ar ? category.name_ar : category.name}
                        </h3>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 bg-[#f8fafc]" data-testid="products-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex justify-between items-end mb-12">
            <div>
              <Badge className="mb-4 bg-[#d4af37] text-[#0f172a]">{t("featured")}</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                {t("popularProducts")}
              </h2>
            </div>
            <Link to="/products" className="hidden md:flex items-center text-[#1a365d] hover:text-[#d4af37] transition-colors font-medium">
              {t("viewAll")}
              <ChevronIcon className={`${isRTL ? 'mr-1' : 'ml-1'} h-5 w-5`} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <div key={i} className="h-80 rounded-xl bg-muted animate-pulse" />
              ))
            ) : (
              featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/products/${product.id}`}>
                    <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 bg-white">
                      <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-20 h-20 text-slate-300" />
                          </div>
                        )}
                        {product.discount > 0 && (
                          <Badge className={`absolute top-4 ${isRTL ? 'right-4' : 'left-4'} bg-red-500 text-white`}>
                            -{product.discount}%
                          </Badge>
                        )}
                        {product.is_featured && (
                          <Badge className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} bg-[#d4af37] text-[#0f172a]`}>
                            {t("featured")}
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-6">
                        <p className="text-xs text-[#d4af37] font-medium uppercase tracking-wide mb-2">
                          {product.brand || "Lynae"}
                        </p>
                        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-[#1a365d] transition-colors line-clamp-2">
                          {isRTL && product.name_ar ? product.name_ar : product.name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <div>
                            {product.discount > 0 ? (
                              <>
                                <span className="text-2xl font-bold text-[#1a365d]">
                                  {(product.sale_price * (1 - product.discount / 100)).toFixed(2)} {t("lyd")}
                                </span>
                                <span className={`text-sm text-muted-foreground line-through ${isRTL ? 'mr-2' : 'ml-2'}`}>
                                  {product.sale_price} {t("lyd")}
                                </span>
                              </>
                            ) : (
                              <span className="text-2xl font-bold text-[#1a365d]">
                                {product.sale_price} {t("lyd")}
                              </span>
                            )}
                          </div>
                          {product.stock_quantity <= product.min_stock_alert && (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              {t("lowStock")}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))
            )}
          </div>
          
          <div className="text-center mt-12 md:hidden">
            <Link to="/products">
              <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90">
                {t("viewAll")}
                <ArrowIcon className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24" data-testid="about-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-[#1a365d]/10 text-[#1a365d]">{t("aboutUs")}</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                {isRTL ? "رائدون في التوزيع الطبي منذ 2012" : "Leading Medical Distribution Since 2012"}
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {t("storyText1")}
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {t("storyText2")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/about">
                  <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90">
                    {isRTL ? "المزيد عنا" : "Learn More"}
                    <ArrowIcon className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
                  </Button>
                </Link>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d] hover:text-white">
                    <Phone className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5`} />
                    {t("contactUs")}
                  </Button>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={IMAGES.aboutProducts}
                  alt="Lynae Products - DHEA, CoQ10, Zinc, Vitamin D"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className={`absolute -bottom-8 ${isRTL ? '-right-8' : '-left-8'} bg-[#d4af37] text-[#0f172a] p-6 rounded-xl shadow-xl`}>
                <p className="text-4xl font-heading font-bold">12+</p>
                <p className="text-sm font-medium">{isRTL ? "سنوات من التميز" : "Years of Excellence"}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1a365d]" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <img 
              src={LOGO_URL} 
              alt="بحر الرمال" 
              className="w-24 h-24 rounded-full object-cover border-4 border-[#d4af37] mx-auto mb-8"
            />
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
              {isRTL ? "هل أنت مستعد للشراكة معنا؟" : "Ready to Partner With Us?"}
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              {isRTL 
                ? "انضم إلى مئات الصيدليات ومقدمي الرعاية الصحية في جميع أنحاء ليبيا الذين يثقون في شركة بحر الرمال لتوريد مستلزماتهم الطبية."
                : "Join hundreds of pharmacies and healthcare providers across Libya who trust Bahr Al Rimal for their medical supplies."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90 h-14 px-8 text-lg font-semibold" data-testid="cta-whatsapp">
                  <svg className={`w-6 h-6 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {isRTL ? "تواصل عبر واتساب" : "Contact via WhatsApp"}
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1a365d] h-14 px-8 text-lg">
                  {t("contactUs")}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partner Logos Marquee */}
      <section className="py-12 bg-slate-50 border-y border-border">
        <Marquee gradient={false} speed={40} direction={isRTL ? "right" : "left"}>
          <div className="flex items-center gap-16 px-8">
            <div className="text-2xl font-heading font-bold text-slate-300">LYNAE</div>
            <div className="text-2xl font-heading font-bold text-slate-300">بحر الرمال</div>
            <div className="text-2xl font-heading font-bold text-slate-300">PHARMA+</div>
            <div className="text-2xl font-heading font-bold text-slate-300">MEDPRO</div>
            <div className="text-2xl font-heading font-bold text-slate-300">SKINCARE+</div>
            <div className="text-2xl font-heading font-bold text-slate-300">BABYCARE</div>
            <div className="text-2xl font-heading font-bold text-slate-300">MEDTECH</div>
            <div className="text-2xl font-heading font-bold text-slate-300">LYNAE</div>
          </div>
        </Marquee>
      </section>

      <Footer />
      
      {/* Fixed WhatsApp Button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110`}
        data-testid="fixed-whatsapp"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default HomePage;
